// src/modules/app-views/crm/components/FormFieldsRenderer.jsx
// *******************************************************************************
// IMPORTS
// *******************************************************************************
import { Flex } from 'antd';
import SimpleInput from 'components/SimpleComponents/SimpleInput';
import SimpleTextArea from 'components/SimpleComponents/SimpleTextArea';
import SimpleNumber from 'components/SimpleComponents/SimpleNumber';
import SimpleCurrency from 'components/SimpleComponents/SimpleCurrency';
import SimpleSelect from 'components/SimpleComponents/SimpleSelect';
import SimpleMultiSelect from 'components/SimpleComponents/SimpleMultiSelect';
import SimpleRadioButtons from 'components/SimpleComponents/SimpleRadioButtons';
import SimpleDatePicker from 'components/SimpleComponents/SimpleDatePicker';
import SimpleSwitch from 'components/SimpleComponents/SimpleSwitch';
import SimplePhone from 'components/SimpleComponents/SimplePhone';
import SimpleEmail from 'components/SimpleComponents/SimpleEmail';
import SimpleWebsite from 'components/SimpleComponents/SimpleWebsite';
import translateX from 'utils/translateX';

// *******************************************************************************
// MAIN COMPONENT
// *******************************************************************************
const FormFieldsRenderer = ({ formFields, columnUid, emptyMessage }) => {
  // ─────────────────────────────────────────────────────────────────────────────
  // 1. LOCAL VARIABLES I
  // ─────────────────────────────────────────────────────────────────────────────
  const sorted = [...(formFields || [])].sort((a, b) => (a.order || 0) - (b.order || 0));

  // ─────────────────────────────────────────────────────────────────────────────
  // 2. UI
  // ─────────────────────────────────────────────────────────────────────────────
  if (sorted.length === 0) {
    return (
      <Flex justify="center" style={{ padding: 48, color: '#999' }}>
        {translateX(emptyMessage || 'no_form_fields')}
      </Flex>
    );
  }

  return sorted.map(field => (
    <div key={field.fieldName}>
      {renderField(field, columnUid)}
    </div>
  ));
};

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default FormFieldsRenderer;

// *******************************************************************************
// HELPERS
// *******************************************************************************
function renderField(field, columnUid) {
  const label = field.title;
  const name = ['formData', field.fieldName];
  const required = (field.requiredInColumns || []).includes(columnUid);
  const placeholder = field.placeholder || undefined;

  switch (field.fieldType) {
    case 'text':
      return <SimpleInput name={name} label={label} required={required} placeholder={placeholder} />;
    case 'textarea':
      return (
        <SimpleTextArea
          name={name}
          label={label}
          required={required}
          placeholder={placeholder}
          rows={field.rows || 3}
        />
      );
    case 'number':
      return (
        <SimpleNumber
          name={name}
          label={label}
          required={required}
          placeholder={placeholder}
          min={field.min}
          max={field.max}
          decimalPlaces={field.decimalPlaces || 0}
        />
      );
    case 'currency':
      return <SimpleCurrency name={name} label={label} required={required} placeholder={placeholder} />;
    case 'select':
      return (
        <SimpleSelect
          name={name}
          label={label}
          required={required}
          placeholder={placeholder}
          options={buildSelectOptions(field.options)}
        />
      );
    case 'multiselect':
      return (
        <SimpleMultiSelect
          name={name}
          label={label}
          required={required}
          placeholder={placeholder}
          options={buildSelectOptions(field.options)}
        />
      );
    case 'radio':
      return (
        <SimpleRadioButtons
          name={name}
          label={label}
          required={required}
          options={buildSelectOptions(field.options)}
        />
      );
    case 'date':
      return <SimpleDatePicker name={name} label={label} required={required} placeholder={placeholder} />;
    case 'switch':
      return <SimpleSwitch name={name} label={label} />;
    case 'phone':
      return <SimplePhone name={name} label={label} required={required} placeholder={placeholder} />;
    case 'email':
      return <SimpleEmail name={name} label={label} required={required} placeholder={placeholder} />;
    case 'url':
      return <SimpleWebsite name={name} label={label} required={required} placeholder={placeholder} />;
    default:
      return <SimpleInput name={name} label={label} required={required} placeholder={placeholder} />;
  }
}

function buildSelectOptions(options) {
  if (!options?.length) return [];
  return options.map(opt => ({ value: opt.value, text: opt.label }));
}
